import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLanguage } from "../../../contexts/LanguageContext";

const FEATURE_KEYS = [
  "appFeature1",
  "appFeature2",
  "appFeature3",
  "appFeature4",
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.15 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
  },
};

const CheckIcon = () => (
  <svg
    className="w-5 h-5 text-white shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

/**
 * Section « JesCook sur le web » : accès partout via le navigateur, sans application à télécharger
 */
const AppDownloadSection = ({ onViewRecipes }) => {
  const { t } = useLanguage();
  return (
    <motion.section
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 lg:py-24 w-full min-w-0"
      initial={false}
      animate="visible"
      variants={containerVariants}
    >
      <div className="bg-black/35 sm:bg-white/10 backdrop-blur-none sm:backdrop-blur rounded-xl sm:rounded-2xl p-5 sm:p-8 md:p-10 lg:p-16 shadow-lg border border-slate-100/50 sm:border-slate-100 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-16 items-center">
          {/* Colonne texte */}
          <div>
            <motion.p
              className="text-xs font-medium tracking-wider uppercase text-white mb-3"
              variants={itemVariants}
            >
              {t("appAccessLabel")}
            </motion.p>
            <motion.h2
              className="text-2xl md:text-3xl font-semibold text-white mb-4 leading-tight"
              variants={itemVariants}
            >
              {t("appWebTitle")}
            </motion.h2>
            <motion.p
              className="text-white text-base mb-8 max-w-md leading-relaxed"
              variants={itemVariants}
            >
              {t("appWebDesc")}
            </motion.p>

            <motion.ul className="space-y-3 mb-10" variants={containerVariants}>
              {FEATURE_KEYS.map((key, i) => (
                <motion.li
                  key={key}
                  variants={itemVariants}
                  className="flex items-start gap-3 text-white text-sm"
                >
                  <CheckIcon />
                  <span>{t(key)}</span>
                </motion.li>
              ))}
            </motion.ul>

            <motion.div
              className="flex flex-wrap gap-3 sm:gap-4"
              variants={itemVariants}
            >
              {onViewRecipes ? (
                <button
                  type="button"
                  onClick={onViewRecipes}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-sm font-semibold rounded-full hover:from-orange-600 hover:to-amber-600 transition-colors"
                >
                  {t("discoverRecipesButton")}
                </button>
              ) : (
                <Link
                  to="/recipes"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-sm font-semibold rounded-full hover:from-orange-600 hover:to-amber-600 transition-colors"
                >
                  {t("discoverRecipesButton")}
                </Link>
              )}
            </motion.div>
          </div>

          {/* Illustration : accès web (ordinateur + mobile navigateur) */}
          <motion.div
            className="flex justify-center lg:justify-end"
            variants={itemVariants}
          >
            <div className="relative w-full max-w-md sm:max-w-lg mx-auto lg:mx-0">
              {/* Fenêtre navigateur (desktop) - plus grande */}
              <div className="rounded-2xl overflow-hidden shadow-2xl border border-slate-200/50 bg-white">
                <div className="h-10 sm:h-12 bg-slate-100 flex items-center gap-2 px-4 border-b border-slate-200">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-slate-300" />
                    <div className="w-3 h-3 rounded-full bg-slate-300" />
                    <div className="w-3 h-3 rounded-full bg-slate-300" />
                  </div>
                  <div className="flex-1 mx-4 py-2 px-4 bg-white rounded text-xs text-slate-400 border border-slate-200">
                    jescook.fr
                  </div>
                </div>
                <div className="p-6 sm:p-8 bg-slate-50">
                  <p className="text-sm sm:text-base font-semibold text-slate-700 mb-4">
                    {t("worldMenusApp")}
                  </p>
                  <div className="space-y-3">
                    {[
                      t("navRecipes"),
                      t("navFavorites"),
                      t("navMyAccount"),
                    ].map((label, i) => (
                      <div
                        key={i}
                        className="h-10 sm:h-12 bg-white rounded-lg border border-slate-100 flex items-center px-4 text-sm text-slate-600"
                      >
                        {label}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Badge "Aucune app" */}
              <div className="absolute -bottom-2 -right-2 sm:right-4 bg-orange-500 text-white text-xs sm:text-sm font-semibold px-4 py-2 rounded-full shadow-lg">
                {t("noDownload")}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default AppDownloadSection;
